"use client"

import { useState, useEffect, useCallback } from "react"

interface TelegramWebApp {
  initData: string
  initDataUnsafe: {
    user?: {
      id: number
      first_name: string
      last_name?: string
      username?: string
      language_code?: string
      photo_url?: string
    }
    auth_date: number
    hash: string
  }
  ready: () => void
  expand: () => void
  close: () => void
  colorScheme: "light" | "dark"
  themeParams: Record<string, string>
  BackButton: {
    show: () => void
    hide: () => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
  }
  MainButton: {
    text: string
    show: () => void
    hide: () => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
    setParams: (params: { text?: string; color?: string }) => void
  }
  HapticFeedback: {
    impactOccurred: (style: string) => void
    notificationOccurred: (type: string) => void
  }
  version: string
  platform: string
  isExpanded: boolean
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp
    }
  }
}

export interface UseTelegramMiniAppResult {
  isMiniApp: boolean
  isReady: boolean
  initData: string | null
  webApp: TelegramWebApp | null
  colorScheme: "light" | "dark" | null
}

/**
 * Detects if running inside Telegram Mini App and provides WebApp SDK access.
 * Dynamically loads the Telegram WebApp JS SDK.
 */
export function useTelegramMiniApp(): UseTelegramMiniAppResult {
  const [isReady, setIsReady] = useState(false)
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null)

  useEffect(() => {
    const initApp = (app: TelegramWebApp) => {
      app.ready()
      app.expand()

      // Mark document for Telegram-specific CSS
      document.documentElement.classList.add("tg-mini-app")

      // Sync color scheme with Telegram theme
      if (app.colorScheme === "dark") {
        document.documentElement.classList.add("dark")
      }

      // Apply Telegram theme colors as CSS variables
      if (app.themeParams) {
        const root = document.documentElement.style
        if (app.themeParams.bg_color) root.setProperty("--tg-bg-color", app.themeParams.bg_color)
        if (app.themeParams.text_color) root.setProperty("--tg-text-color", app.themeParams.text_color)
      }

      setWebApp(app)
      setIsReady(true)
    }

    // Check if already loaded
    if (window.Telegram?.WebApp?.initData) {
      initApp(window.Telegram.WebApp)
      return
    }

    // Only load the SDK inside a real Telegram WebView — in a plain browser
    // the script spams console with postEvent noise and does nothing useful.
    // Detection WITHOUT the SDK: native clients inject TelegramWebviewProxy;
    // Telegram Web passes tgWebApp* params in the URL hash; the SDK itself
    // persists init params in sessionStorage across in-app navigations.
    const inTelegramWebView =
      typeof (window as { TelegramWebviewProxy?: unknown }).TelegramWebviewProxy !== "undefined"
      || window.location.hash.includes("tgWebApp")
      || (() => {
        try {
          const params = sessionStorage.getItem("__telegram__initParams")
          if (!params) return false
          const parsed = JSON.parse(params) as Record<string, unknown>
          // The SDK writes this key even OUTSIDE Telegram (empty values) —
          // only non-empty init data proves a real WebView session.
          return !!(parsed.tgWebAppData || parsed.tgWebAppPlatform)
        } catch {
          return false
        }
      })()
    if (!inTelegramWebView) {
      setIsReady(true)
      return
    }

    // Load Telegram WebApp SDK dynamically
    const script = document.createElement("script")
    script.src = "https://telegram.org/js/telegram-web-app.js"
    script.async = true
    script.onload = () => {
      if (window.Telegram?.WebApp?.initData) {
        initApp(window.Telegram.WebApp)
      }
    }
    document.head.appendChild(script)

    return () => {
      // Don't remove the script on unmount — it's global
    }
  }, [])

  const isMiniApp = !!webApp?.initData
  const initData = webApp?.initData || null
  const colorScheme = webApp?.colorScheme || null

  return { isMiniApp, isReady, initData, webApp, colorScheme }
}
